/**
 * Number Schema Management Page
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Pencil, Trash2, Copy, CheckCircle2, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useDateFormat } from "@/hooks/use-date-format";
import {
  useNumberSchemas,
  useDeleteNumberSchema,
} from "@/features/number-schema/hooks/use-number-schema";
import {
  SCHEMA_TYPE_LABELS,
  COUNTER_RESET_LABELS,
  type NumberSchema,
  type SchemaType,
} from "@/features/number-schema/types/number-schema.types";
import { NumberSchemaDialog } from "@/features/number-schema/components/number-schema-dialog";

export const Route = createFileRoute(
  "/(app)/dashboard/settings/number-schemas",
)({
  component: NumberSchemasPage,
});

function NumberSchemasPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormat();
  const [selectedType, setSelectedType] = useState<SchemaType>("invoice");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchema, setEditingSchema] = useState<NumberSchema | null>(null);
  const [deleteSchema, setDeleteSchema] = useState<NumberSchema | null>(null);

  useDocumentTitle("Nummerierungsschemas");

  const { data: schemas, isLoading } = useNumberSchemas(selectedType);
  const deleteSchemaМutation = useDeleteNumberSchema();

  const handleCreate = () => {
    setEditingSchema(null);
    setDialogOpen(true);
  };

  const handleEdit = (schema: NumberSchema) => {
    setEditingSchema(schema);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteSchema) return;

    try {
      await deleteSchemaМutation.mutateAsync(deleteSchema.id);
      toast({
        title: "Erfolgreich gelöscht",
        description: "Das Nummerierungsschema wurde gelöscht.",
      });
      setDeleteSchema(null);
    } catch {
      toast({
        title: "Fehler",
        description: "Das Schema konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  const handleDuplicate = (schema: NumberSchema) => {
    setEditingSchema({
      ...schema,
      id: "",
      name: `${schema.name} (Kopie)`,
      isDefault: false,
    } as NumberSchema);
    setDialogOpen(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // Reset editing schema when dialog closes
      setEditingSchema(null);
    }
  };

  return (
    <div className="container mx-auto space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Nummerierungsschemas</h1>
          <p className="text-muted-foreground mt-1">
            Verwalten Sie Schemas für Rechnungs-, Kunden- und andere Nummern
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Neues Schema
        </Button>
      </div>

      {/* Type Tabs */}
      <div className="flex gap-2">
        {(["invoice", "customer", "offer", "delivery"] as SchemaType[]).map(
          (type) => (
            <Button
              key={type}
              variant={selectedType === type ? "default" : "outline"}
              onClick={() => setSelectedType(type)}
            >
              {SCHEMA_TYPE_LABELS[type]}
            </Button>
          ),
        )}
      </div>

      {/* Schemas Table */}
      <Card>
        <CardHeader>
          <CardTitle>{SCHEMA_TYPE_LABELS[selectedType]}-Schemas</CardTitle>
          <CardDescription>
            Schemas für {SCHEMA_TYPE_LABELS[selectedType].toLowerCase()}nummern
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">Laden...</p>
            </div>
          ) : !schemas || schemas.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">
                Keine Schemas vorhanden. Erstellen Sie Ihr erstes Schema.
              </p>
              <Button onClick={handleCreate} className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                Erstes Schema erstellen
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Zähler-Reset</TableHead>
                  <TableHead>Gültig ab</TableHead>
                  <TableHead>Gültig bis</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schemas.map((schema) => (
                  <TableRow key={schema.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {schema.isDefault ? (
                          <Badge variant="default">
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Standard
                          </Badge>
                        ) : (
                          <Circle className="text-muted-foreground h-4 w-4" />
                        )}
                        {schema.isSystemGenerated && (
                          <Badge
                            variant="outline"
                            className="border-blue-500 text-blue-700"
                          >
                            System
                          </Badge>
                        )}
                        {schema.isUsed && (
                          <Badge
                            variant="outline"
                            className="border-amber-500 text-amber-700"
                          >
                            In Verwendung
                          </Badge>
                        )}
                        {!schema.isActive && (
                          <Badge variant="secondary">Inaktiv</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{schema.name}</TableCell>
                    <TableCell>
                      <code className="bg-muted rounded px-2 py-1 text-sm">
                        {schema.template}
                      </code>
                    </TableCell>
                    <TableCell>
                      {COUNTER_RESET_LABELS[schema.counterReset]}
                    </TableCell>
                    <TableCell>{formatDate(schema.validFrom)}</TableCell>
                    <TableCell>
                      {schema.validUntil ? formatDate(schema.validUntil) : "∞"}
                    </TableCell>
                    <TableCell>
                      {schema.customerId ? (
                        <Badge variant="outline">Kundenspezifisch</Badge>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(schema)}
                          title={
                            schema.isUsed
                              ? "Schema ansehen"
                              : "Schema bearbeiten"
                          }
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDuplicate(schema)}
                          title="Schema duplizieren"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {!schema.isSystemGenerated && !schema.isUsed && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteSchema(schema)}
                            title="Schema löschen"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <NumberSchemaDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        schema={editingSchema}
        defaultType={selectedType}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteSchema}
        onOpenChange={() => setDeleteSchema(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Schema löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie das Schema "{deleteSchema?.name}" wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
              {deleteSchema?.isDefault && (
                <p className="mt-2 font-semibold text-orange-600">
                  Achtung: Dies ist das Standard-Schema!
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
